import { useCallback, useEffect, useState } from "react";
import { Download, RefreshCw, Trash2 } from "lucide-react";
import { apiFetch } from "./api";

const COLUMNS = [
    { key: "timestamp", label: "Date" },
    { key: "principal_name", label: "Principal / GM" },
    { key: "dealership_name", label: "Dealership" },
    { key: "location", label: "Location" },
    { key: "cell_phone", label: "Cell" },
    { key: "email", label: "Email" },
    { key: "retail_units_per_month", label: "Units/mo" },
    { key: "ros_per_month", label: "ROs/mo" },
];

const formatDate = (ts) => {
    try {
        return new Date(ts).toLocaleString();
    } catch {
        return ts;
    }
};

export const Leads = ({ token, onAuthError }) => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await apiFetch("/dealer-inquiries", { token });
            setLeads(data);
        } catch (err) {
            if (err.status === 401) return onAuthError();
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token, onAuthError]);

    useEffect(() => {
        load();
    }, [load]);

    const remove = async (id) => {
        try {
            await apiFetch(`/dealer-inquiries/${id}`, {
                token,
                method: "DELETE",
            });
            setLeads((ls) => ls.filter((l) => l.id !== id));
        } catch (err) {
            if (err.status === 401) return onAuthError();
            setError(err.message);
        }
    };

    const exportCsv = () => {
        const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const rows = leads.map((l) =>
            COLUMNS.map((c) => esc(l[c.key])).join(","),
        );
        const csv = [COLUMNS.map((c) => esc(c.label)).join(","), ...rows].join(
            "\n",
        );
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        a.download = "advantashield-dealer-leads.csv";
        a.click();
        URL.revokeObjectURL(a.href);
    };

    return (
        <div data-testid="admin-leads-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-slate-400">
                    {leads.length} {leads.length === 1 ? "inquiry" : "inquiries"}
                </p>
                <div className="flex gap-2">
                    <button
                        data-testid="leads-refresh-btn"
                        onClick={load}
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white transition-colors hover:border-sky-400/50"
                    >
                        <RefreshCw className="h-3.5 w-3.5" /> Refresh
                    </button>
                    <button
                        data-testid="leads-export-btn"
                        onClick={exportCsv}
                        disabled={leads.length === 0}
                        className="inline-flex items-center gap-2 rounded-full bg-sky-400 px-4 py-2 text-xs font-bold text-[#0A0D14] transition-colors hover:bg-sky-300 disabled:opacity-50"
                    >
                        <Download className="h-3.5 w-3.5" /> Export CSV
                    </button>
                </div>
            </div>

            {error && (
                <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                    {error}
                </p>
            )}

            <div className="mt-5 overflow-x-auto rounded-2xl border border-white/10">
                <table className="w-full min-w-[900px] text-left text-sm">
                    <thead>
                        <tr className="border-b border-white/10 bg-[#182030]">
                            {COLUMNS.map((c) => (
                                <th
                                    key={c.key}
                                    className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400"
                                >
                                    {c.label}
                                </th>
                            ))}
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td
                                    colSpan={COLUMNS.length + 1}
                                    className="px-4 py-10 text-center text-slate-500"
                                >
                                    Loading…
                                </td>
                            </tr>
                        ) : leads.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={COLUMNS.length + 1}
                                    className="px-4 py-10 text-center text-slate-500"
                                >
                                    No dealer inquiries yet.
                                </td>
                            </tr>
                        ) : (
                            leads.map((l) => (
                                <tr
                                    key={l.id}
                                    data-testid={`lead-row-${l.id}`}
                                    className="border-b border-white/5 transition-colors hover:bg-white/[0.03]"
                                >
                                    {COLUMNS.map((c) => (
                                        <td
                                            key={c.key}
                                            className="max-w-[180px] truncate px-4 py-3 text-slate-300"
                                        >
                                            {c.key === "timestamp"
                                                ? formatDate(l[c.key])
                                                : l[c.key]}
                                        </td>
                                    ))}
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            data-testid={`lead-delete-${l.id}`}
                                            onClick={() => remove(l.id)}
                                            className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-300"
                                            aria-label="Delete inquiry"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
